import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  Video,
  Phone,
  MapPin,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  Calendar,
  Globe,
  User,
} from "lucide-react";
import { publicBookingApi } from "../services/api";
import KavoaLogo from "../assets/KavoaLogo.png";

// ─── Types ───────────────────────────────────────────────────────────────────

interface BookingQuestion {
  label: string;
  type: "text" | "textarea" | "select";
  required: boolean;
  options?: string[];
}

interface BookingTypeData {
  _id: string;
  name: string;
  slug: string;
  duration: number;
  location: { type: "video" | "phone" | "in_person" | "custom"; details?: string | null };
  description?: string;
  color?: string;
  questions: BookingQuestion[];
  collectPhone?: boolean;
  collectMessage?: boolean;
  confirmationMessage?: string;
}

interface TeamMemberData {
  name: string;
  email?: string;
}

interface FirmBrandingData {
  name: string;
  slug?: string;
  branding?: any;
}

interface BookingData {
  firm: FirmBrandingData;
  bookingType: BookingTypeData;
  teamMember: TeamMemberData;
}

interface SlotDay {
  date: string;
  slots: string[];
}

interface BookingResult {
  booking: {
    _id: string;
    status: string;
    startAt: string;
    endAt: string;
    manageToken: string;
  };
  meetingUrl?: string | null;
  manageUrl: string;
  confirmationMessage?: string;
}

type Phase = "loading" | "date" | "time" | "form" | "confirmation" | "error";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateICS(
  title: string,
  start: Date,
  end: Date,
  location?: string,
  description?: string
): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const formatDate = (d: Date) =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Kavoa//Booking//EN",
    "BEGIN:VEVENT",
    `DTSTART:${formatDate(start)}`,
    `DTEND:${formatDate(end)}`,
    `SUMMARY:${title}`,
    location ? `LOCATION:${location}` : "",
    description ? `DESCRIPTION:${description}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

function formatSlotTime(isoOrTime: string): string {
  // Slots are now ISO strings (e.g., "2026-03-03T09:15:00.000-05:00")
  const d = new Date(isoOrTime);
  if (!isNaN(d.getTime())) {
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  // Fallback for "HH:mm" format
  const [hourStr, minuteStr] = isoOrTime.split(":");
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr;
  const ampm = hour >= 12 ? "PM" : "AM";
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minute} ${ampm}`;
}

function formatDateReadable(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Toronto";
  }
}

function getLocationIcon(locationType: string | undefined) {
  switch (locationType) {
    case "video":
      return Video;
    case "phone":
      return Phone;
    case "in_person":
      return MapPin;
    default:
      return Video;
  }
}

function getLocationLabel(locationType: string | undefined, detail?: string): string {
  switch (locationType) {
    case "video":
      return detail || "Video Call";
    case "phone":
      return detail || "Phone Call";
    case "in_person":
      return detail || "In Person";
    default:
      return detail || "Meeting";
  }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isSameDay(d1: string, d2: string): boolean {
  return d1 === d2;
}

function isBeforeToday(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return d < today;
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Slots are now full ISO strings from the backend, so no conversion needed

// ─── Sub-components ──────────────────────────────────────────────────────────

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="text-center space-y-4"
    >
      <Loader2 className="h-10 w-10 text-slate-400 animate-spin mx-auto" />
      <p className="text-sm text-slate-500">Loading booking page...</p>
    </motion.div>
  </div>
);

const ErrorState: React.FC<{ message?: string }> = ({ message }) => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center space-y-4 max-w-md"
    >
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center">
          <AlertCircle className="h-8 w-8 text-red-500" />
        </div>
      </div>
      <h1 className="text-2xl font-bold text-slate-800">
        Booking Page Not Found
      </h1>
      <p className="text-slate-500">
        {message ||
          "This booking page doesn't exist or is no longer available. Please check the link and try again."}
      </p>
    </motion.div>
  </div>
);

// ─── Mini Calendar ───────────────────────────────────────────────────────────

const MiniCalendar: React.FC<{
  currentMonth: Date;
  onMonthChange: (d: Date) => void;
  availableDates: Set<string>;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  accentColor: string;
}> = ({
  currentMonth,
  onMonthChange,
  availableDates,
  selectedDate,
  onSelectDate,
  accentColor,
}) => {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const todayISO = getTodayISO();

  const monthLabel = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () => {
    const prev = new Date(year, month - 1, 1);
    const now = new Date();
    // Don't go before current month
    if (prev.getFullYear() < now.getFullYear() || (prev.getFullYear() === now.getFullYear() && prev.getMonth() < now.getMonth())) {
      return;
    }
    onMonthChange(prev);
  };

  const nextMonth = () => {
    onMonthChange(new Date(year, month + 1, 1));
  };

  const canGoPrev = (() => {
    const prev = new Date(year, month - 1, 1);
    const now = new Date();
    return !(prev.getFullYear() < now.getFullYear() || (prev.getFullYear() === now.getFullYear() && prev.getMonth() < now.getMonth()));
  })();

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(d);
  }

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          disabled={!canGoPrev}
          className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <span className="text-sm font-semibold text-slate-800">
          {monthLabel}
        </span>
        <button
          onClick={nextMonth}
          className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ChevronRight className="h-5 w-5 text-slate-600" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-2">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div
            key={i}
            className="text-center text-xs font-medium text-slate-400 py-1"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} />;
          }

          const dateISO = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const isPast = isBeforeToday(dateISO);
          const hasSlots = availableDates.has(dateISO);
          const isSelected = selectedDate ? isSameDay(dateISO, selectedDate) : false;
          const isToday = dateISO === todayISO;
          const isClickable = !isPast && hasSlots;

          return (
            <button
              key={dateISO}
              onClick={() => isClickable && onSelectDate(dateISO)}
              disabled={!isClickable}
              className={`
                relative flex flex-col items-center justify-center h-10 w-full rounded-lg text-sm transition-all
                ${isSelected ? "text-white font-bold" : ""}
                ${!isSelected && isClickable ? "text-slate-700 font-medium hover:bg-slate-100 cursor-pointer" : ""}
                ${!isClickable ? "text-slate-300 cursor-default" : ""}
                ${isToday && !isSelected ? "ring-1 ring-slate-300" : ""}
              `}
              style={
                isSelected
                  ? { backgroundColor: accentColor }
                  : undefined
              }
            >
              <span>{day}</span>
              {hasSlots && !isSelected && (
                <span
                  className="absolute bottom-1 h-1 w-1 rounded-full"
                  style={{ backgroundColor: accentColor }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Booking Info Sidebar ────────────────────────────────────────────────────

const BookingInfo: React.FC<{
  bookingData: BookingData;
  accentColor: string;
}> = ({ bookingData, accentColor }) => {
  const { firm, bookingType, teamMember } = bookingData;
  const LocationIcon = getLocationIcon(bookingType.location?.type);
  const locationLabel = getLocationLabel(
    bookingType.location?.type,
    bookingType.location?.details || undefined
  );

  return (
    <div className="space-y-4">
      <p className="text-lg font-bold text-slate-400">{firm.name}</p>
      <h2 className="text-xl font-bold text-slate-800">{teamMember.name}</h2>
      <p className="text-lg font-semibold capitalize" style={{ color: accentColor }}>
        {bookingType.name}
      </p>

      <div className="space-y-2.5 pt-1">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Clock className="h-4 w-4 text-slate-400" />
          <span>{bookingType.duration} min</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <LocationIcon className="h-4 w-4 text-slate-400" />
          <span>{locationLabel}</span>
        </div>
      </div>

      {bookingType.description && (
        <p className="text-sm text-slate-600 pt-1 leading-relaxed">
          {bookingType.description}
        </p>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BookingPage: React.FC = () => {
  const { firmSlug, bookingSlug } = useParams<{
    firmSlug: string;
    bookingSlug: string;
  }>();

  // ─── State ──────────────────────────────────────────────────────────────────

  const [phase, setPhase] = useState<Phase>("loading");
  const [bookingData, setBookingData] = useState<BookingData | null>(null);
  const [slots, setSlots] = useState<SlotDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [slotError, setSlotError] = useState<string | null>(null);

  const timezone = useMemo(() => getTimezone(), []);

  const accentColor = bookingData?.bookingType?.color || "#0F5132";

  // ─── Available dates set (for calendar) ─────────────────────────────────────

  const availableDates = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => {
      if (s.slots.length > 0) {
        set.add(s.date);
      }
    });
    return set;
  }, [slots]);

  // ─── Selected date's available times ────────────────────────────────────────

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    const day = slots.find((s) => s.date === selectedDate);
    return day ? day.slots : [];
  }, [slots, selectedDate]);

  // ─── Fetch booking type on mount ────────────────────────────────────────────

  const fetchSlots = useCallback(
    async (monthDate: Date) => {
      if (!firmSlug || !bookingSlug) return;
      const startDate = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}-01`;
      // Get up to 6 weeks out from the start of the month
      const endDate = (() => {
        const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 2, 0);
        return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
      })();

      try {
        const res = await publicBookingApi.getSlots(firmSlug, bookingSlug, {
          startDate,
          endDate,
        });
        setSlots(res.data.data || res.data.slots || []);
      } catch {
        // Slots fetch failure is non-fatal; calendar will show no available days
        setSlots([]);
      }
    },
    [firmSlug, bookingSlug]
  );

  useEffect(() => {
    const init = async () => {
      if (!firmSlug || !bookingSlug) {
        setPhase("error");
        return;
      }

      try {
        const res = await publicBookingApi.getBookingType(firmSlug, bookingSlug);
        setBookingData(res.data);

        // Fetch slots for current month
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        setCurrentMonth(monthStart);
        await fetchSlots(monthStart);

        setPhase("date");
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError("not_found");
        } else {
          setError("Failed to load booking page. Please try again later.");
        }
        setPhase("error");
      }
    };

    init();
  }, [firmSlug, bookingSlug, fetchSlots]);

  // ─── Refetch slots on month change ──────────────────────────────────────────

  const handleMonthChange = useCallback(
    (newMonth: Date) => {
      setCurrentMonth(newMonth);
      fetchSlots(newMonth);
    },
    [fetchSlots]
  );

  // ─── Phase transitions ─────────────────────────────────────────────────────

  const handleSelectDate = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setPhase("time");
  };

  const handleSelectTime = (time: string) => {
    setSelectedTime(time);
    setPhase("form");
  };

  const handleBackToDate = () => {
    setSelectedTime(null);
    setSlotError(null);
    setPhase("date");
  };

  const handleBackToTime = () => {
    setSlotError(null);
    setPhase("time");
  };

  // ─── Form submission ───────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firmSlug || !bookingSlug || !selectedDate || !selectedTime) return;

    setSubmitting(true);
    setSlotError(null);

    try {
      // selectedTime is now a full ISO string from the backend (e.g., "2026-03-03T09:15:00.000-05:00")
      const startAt = selectedTime;

      const formattedAnswers: { questionLabel: string; answer: string }[] = [];

      // Add message as an answer if collected
      if (bookingData?.bookingType.collectMessage !== false && message.trim()) {
        formattedAnswers.push({
          questionLabel: "Message",
          answer: message.trim(),
        });
      }

      // Add custom question answers
      bookingData?.bookingType.questions.forEach((q) => {
        const val = answers[q.label] || "";
        if (val) {
          formattedAnswers.push({
            questionLabel: q.label,
            answer: val,
          });
        }
      });

      const res = await publicBookingApi.submitBooking(firmSlug, bookingSlug, {
        bookedByName: name.trim(),
        bookedByEmail: email.trim(),
        bookedByPhone: phone.trim() || undefined,
        startAt,
        timezone,
        answers: formattedAnswers,
      });

      setBookingResult(res.data);
      setPhase("confirmation");
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSlotError(
          "This time slot is no longer available. Please select another time."
        );
        setPhase("time");
        // Re-fetch slots to get updated availability
        fetchSlots(currentMonth);
      } else {
        setSlotError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Something went wrong. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── ICS download ──────────────────────────────────────────────────────────

  const handleDownloadICS = () => {
    if (!bookingResult || !bookingData) return;

    const start = new Date(bookingResult.booking.startAt);
    const end = new Date(bookingResult.booking.endAt);
    const title = `${bookingData.bookingType.name} with ${bookingData.teamMember.name}`;
    const location = getLocationLabel(
      bookingData.bookingType.location?.type,
      bookingData.bookingType.location?.details || undefined
    );

    const icsContent = generateICS(title, start, end, location);
    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "booking.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ─── Renders ───────────────────────────────────────────────────────────────

  if (phase === "loading") return <LoadingState />;
  if (phase === "error") return <ErrorState message={error || undefined} />;
  if (!bookingData) return <ErrorState />;

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        {/* Firm branding header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 text-center sm:text-left"
        >
          <div className="inline-flex items-center gap-2.5">
            <div
              className="h-9 w-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: accentColor }}
            >
              <span className="text-white font-bold text-sm">
                {bookingData.firm.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="font-bold text-slate-800 text-lg">
              {bookingData.firm.name}
            </span>
          </div>
        </motion.div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
        >
          <AnimatePresence mode="wait">
            {/* ─── Phase: Date Selection ────────────────────────────────────── */}
            {phase === "date" && (
              <motion.div
                key="date"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col md:flex-row"
              >
                {/* Left: Booking info */}
                <div className="p-6 sm:p-8 md:w-[45%] md:border-r border-b md:border-b-0 border-slate-200">
                  <BookingInfo
                    bookingData={bookingData}
                    accentColor={accentColor}
                  />
                </div>

                {/* Right: Calendar */}
                <div className="p-6 sm:p-8 md:w-[55%]">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4">
                    Select a Date
                  </h3>
                  <MiniCalendar
                    currentMonth={currentMonth}
                    onMonthChange={handleMonthChange}
                    availableDates={availableDates}
                    selectedDate={selectedDate}
                    onSelectDate={handleSelectDate}
                    accentColor={accentColor}
                  />
                </div>
              </motion.div>
            )}

            {/* ─── Phase: Time Selection ────────────────────────────────────── */}
            {phase === "time" && (
              <motion.div
                key="time"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col md:flex-row"
              >
                {/* Left: Booking info */}
                <div className="p-6 sm:p-8 md:w-[45%] md:border-r border-b md:border-b-0 border-slate-200">
                  <BookingInfo
                    bookingData={bookingData}
                    accentColor={accentColor}
                  />
                </div>

                {/* Right: Time slots */}
                <div className="p-6 sm:p-8 md:w-[55%]">
                  <button
                    onClick={handleBackToDate}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-4"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>

                  <h3 className="text-sm font-semibold text-slate-800 mb-1">
                    {selectedDate && formatDateReadable(selectedDate)}
                  </h3>
                  <p className="text-xs text-slate-400 mb-4 flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    Times shown in {timezone.replace(/_/g, " ")}
                  </p>

                  {slotError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {slotError}
                    </div>
                  )}

                  {selectedDateSlots.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      No times available for this date. Please select another
                      date.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto pr-1">
                      {selectedDateSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => handleSelectTime(time)}
                          className="px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 transition-all cursor-pointer"
                          style={{
                            ...(selectedTime === time
                              ? {
                                  borderColor: accentColor,
                                  color: accentColor,
                                  backgroundColor: `${accentColor}10`,
                                }
                              : {}),
                          }}
                          onMouseEnter={(e) => {
                            if (selectedTime !== time) {
                              (e.currentTarget as HTMLButtonElement).style.borderColor = accentColor;
                              (e.currentTarget as HTMLButtonElement).style.color = accentColor;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedTime !== time) {
                              (e.currentTarget as HTMLButtonElement).style.borderColor = "#e2e8f0";
                              (e.currentTarget as HTMLButtonElement).style.color = "#334155";
                            }
                          }}
                        >
                          {formatSlotTime(time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ─── Phase: Intake Form ──────────────────────────────────────── */}
            {phase === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="flex flex-col md:flex-row"
              >
                {/* Left: Booking info + selected date/time */}
                <div className="p-6 sm:p-8 md:w-[45%] md:border-r border-b md:border-b-0 border-slate-200">
                  <BookingInfo
                    bookingData={bookingData}
                    accentColor={accentColor}
                  />

                  <div className="mt-6 pt-4 border-t border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">
                        {selectedDate && formatDateReadable(selectedDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">
                        {selectedTime && formatSlotTime(selectedTime)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Form */}
                <div className="p-6 sm:p-8 md:w-[55%]">
                  <button
                    onClick={handleBackToTime}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-4"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>

                  <h3 className="text-sm font-semibold text-slate-800 mb-4">
                    Enter Your Details
                  </h3>

                  {slotError && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-4">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {slotError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your full name"
                          className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                      />
                    </div>

                    {/* Phone (conditional) */}
                    {bookingData.bookingType.collectPhone !== false && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="(optional)"
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                        />
                      </div>
                    )}

                    {/* Message (conditional) */}
                    {bookingData.bookingType.collectMessage !== false && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Message
                        </label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={3}
                          placeholder="Anything you'd like us to know before the meeting (optional)"
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 resize-none"
                        />
                      </div>
                    )}

                    {/* Custom questions */}
                    {bookingData.bookingType.questions.map((q) => (
                      <div key={q.label}>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {q.label}{" "}
                          {q.required && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>

                        {q.type === "text" && (
                          <input
                            type="text"
                            required={q.required}
                            value={answers[q.label] || ""}
                            onChange={(e) =>
                              setAnswers((prev) => ({
                                ...prev,
                                [q.label]: e.target.value,
                              }))
                            }
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                          />
                        )}

                        {q.type === "textarea" && (
                          <textarea
                            required={q.required}
                            value={answers[q.label] || ""}
                            onChange={(e) =>
                              setAnswers((prev) => ({
                                ...prev,
                                [q.label]: e.target.value,
                              }))
                            }
                            rows={3}
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 resize-none"
                          />
                        )}

                        {q.type === "select" && (
                          <select
                            required={q.required}
                            value={answers[q.label] || ""}
                            onChange={(e) =>
                              setAnswers((prev) => ({
                                ...prev,
                                [q.label]: e.target.value,
                              }))
                            }
                            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 bg-white"
                          >
                            <option value="">Select...</option>
                            {q.options?.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full text-white rounded-xl py-3.5 font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-70"
                      style={{ backgroundColor: accentColor }}
                      onMouseEnter={(e) => {
                        if (!submitting)
                          (e.currentTarget as HTMLButtonElement).style.opacity = "0.9";
                      }}
                      onMouseLeave={(e) => {
                        if (!submitting)
                          (e.currentTarget as HTMLButtonElement).style.opacity = "1";
                      }}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Scheduling...
                        </>
                      ) : (
                        "Schedule Meeting"
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            )}

            {/* ─── Phase: Confirmation ─────────────────────────────────────── */}
            {phase === "confirmation" && bookingResult && (
              <motion.div
                key="confirmation"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="p-8 sm:p-12 text-center"
              >
                {/* Animated checkmark */}
                <div className="flex justify-center mb-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: 0.15,
                    }}
                    className="h-20 w-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${accentColor}15` }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 15,
                        delay: 0.3,
                      }}
                    >
                      <Check
                        className="h-10 w-10"
                        style={{ color: accentColor }}
                        strokeWidth={3}
                      />
                    </motion.div>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">
                      Booking Confirmed!
                    </h2>
                    <p className="text-slate-500">
                      A confirmation email has been sent to {email}.
                    </p>
                  </div>

                  {/* Meeting summary card */}
                  <div className="inline-block bg-slate-50 rounded-xl border border-slate-200 p-6 text-left space-y-3 w-full max-w-sm mx-auto">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">
                        {formatDateReadable(
                          new Date(bookingResult.booking.startAt).toISOString().split("T")[0]
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">
                        {new Date(bookingResult.booking.startAt).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          }
                        )}{" "}
                        - {bookingData.bookingType.duration} min
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Globe className="h-4 w-4 text-slate-400" />
                      <span className="text-xs">
                        {timezone.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      {React.createElement(
                        getLocationIcon(bookingData.bookingType.location?.type),
                        { className: "h-4 w-4 text-slate-400" }
                      )}
                      <span>
                        {getLocationLabel(
                          bookingData.bookingType.location?.type,
                          bookingData.bookingType.location?.details || undefined
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <User className="h-4 w-4 text-slate-400" />
                      <span>
                        {bookingData.teamMember.name}
                      </span>
                    </div>
                  </div>

                  {/* Video Meeting Link */}
                  {bookingResult.meetingUrl && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100 text-center">
                      <p className="text-xs font-semibold text-blue-700 mb-2">Video Meeting Link</p>
                      <a
                        href={bookingResult.meetingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors"
                      >
                        <Video className="h-4 w-4" />
                        Join Meeting
                      </a>
                      <p className="text-xs text-slate-500 mt-2 break-all">
                        {bookingResult.meetingUrl}
                      </p>
                    </div>
                  )}

                  {/* Custom confirmation message */}
                  {bookingData.bookingType.confirmationMessage && (
                    <p className="text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
                      {bookingData.bookingType.confirmationMessage}
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                    <button
                      onClick={handleDownloadICS}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Calendar className="h-4 w-4" />
                      Add to Calendar
                    </button>

                    {bookingResult.manageUrl && (
                      <a
                        href={bookingResult.manageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium transition-colors"
                        style={{ color: accentColor }}
                      >
                        Manage Booking
                      </a>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <div className="text-center mt-6 space-y-1">
          <p className="text-xs text-slate-400">Powered by</p>
          <a
            href="https://kavoa.io"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col items-center gap-1 group"
          >
            <img src={KavoaLogo} alt="Kavoa" className="h-10 object-contain" />
            <span className="text-[10px] text-slate-400 group-hover:text-slate-600 transition-colors">
              kavoa.io
            </span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
